/**
 * Chunked file upload helper.
 *
 * Strategy:
 *   - Files < CHUNKED_THRESHOLD are expected to use the legacy base64+JSON path.
 *     This helper is for files >= CHUNKED_THRESHOLD.
 *   - Splits file into ~5MB chunks, uploads sequentially with per-chunk retry
 *     (3 attempts, exponential backoff 1s/2s/4s).
 *   - Emits progress via onProgress({ loaded, total, percent }).
 *   - Supports abort via AbortController (pass { signal }).
 *
 * Returns the `file_reference` dict from POST /api/uploads/chunks/{id}/complete,
 * which the caller can drop directly into a FileUpload.uploaded_refs array.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CHUNK_SIZE = 5 * 1024 * 1024;          // 5 MB
export const CHUNKED_THRESHOLD = 10 * 1024 * 1024;   // use chunked for > 10 MB
const MAX_RETRIES = 3;

const authHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function uploadChunkWithRetry({ uploadId, index, blob, signal }) {
  let lastError;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (signal?.aborted) throw new Error("aborted");
    try {
      const form = new FormData();
      form.append("chunk_index", String(index));
      form.append("chunk", blob, `chunk_${index}`);
      const resp = await axios.post(`${API}/uploads/chunks/${uploadId}`, form, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        signal,
        timeout: 120000,
      });
      return resp.data;
    } catch (err) {
      lastError = err;
      if (signal?.aborted || err.name === "CanceledError" || err.code === "ERR_CANCELED") {
        throw err;
      }
      // backoff
      if (attempt < MAX_RETRIES - 1) {
        await sleep(1000 * 2 ** attempt);
      }
    }
  }
  throw lastError || new Error(`Chunk ${index} failed after ${MAX_RETRIES} attempts`);
}

/**
 * Upload a single File using the chunked endpoints.
 *
 * @param {File} file
 * @param {Object} opts
 * @param {(p:{loaded:number,total:number,percent:number}) => void} [opts.onProgress]
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<Object>} file_reference
 */
export async function uploadFileChunked(file, opts = {}) {
  const { onProgress, signal } = opts;
  const totalSize = file.size;
  const totalChunks = Math.max(1, Math.ceil(totalSize / CHUNK_SIZE));

  // 1. init
  const { data: initData } = await axios.post(
    `${API}/uploads/chunks/init`,
    {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      total_size: totalSize,
      total_chunks: totalChunks,
    },
    { headers: { ...authHeader(), "Content-Type": "application/json" }, signal },
  );
  const uploadId = initData.upload_id;

  // 2. append chunks sequentially
  let loaded = 0;
  onProgress && onProgress({ loaded: 0, total: totalSize, percent: 0 });
  try {
    for (let i = 0; i < totalChunks; i++) {
      if (signal?.aborted) throw new Error("aborted");
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, totalSize);
      const blob = file.slice(start, end);
      await uploadChunkWithRetry({ uploadId, index: i, blob, signal });
      loaded = end;
      if (onProgress) {
        onProgress({
          loaded,
          total: totalSize,
          percent: Math.round((loaded / totalSize) * 100),
        });
      }
    }
  } catch (err) {
    // Best-effort abort of server-side upload
    try {
      await axios.delete(`${API}/uploads/chunks/${uploadId}`, { headers: authHeader() });
    } catch (_) { /* ignore */ }
    throw err;
  }

  // 3. complete
  const { data: completeData } = await axios.post(
    `${API}/uploads/chunks/${uploadId}/complete`,
    {},
    { headers: authHeader(), signal },
  );
  if (onProgress) {
    onProgress({ loaded: totalSize, total: totalSize, percent: 100 });
  }
  return completeData.file_reference;
}

/**
 * Convert a File to base64 data URL (legacy path for small files).
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Upload an array of File objects, using chunked upload for large files and
 * base64 for small ones. Returns { files: string[], uploaded_refs: object[] }.
 *
 * @param {File[]} files
 * @param {Object} opts
 * @param {(p:{fileIndex:number,fileName:string,loaded:number,total:number,percent:number,overallPercent:number}) => void} [opts.onProgress]
 * @param {AbortSignal} [opts.signal]
 */
export async function uploadFilesMixed(files, opts = {}) {
  const { onProgress, signal } = opts;
  const base64Files = [];
  const uploadedRefs = [];
  const totalBytes = files.reduce((s, f) => s + f.size, 0) || 1;
  let bytesDoneBefore = 0;

  for (let fi = 0; fi < files.length; fi++) {
    const f = files[fi];
    if (signal?.aborted) throw new Error("aborted");
    if (f.size >= CHUNKED_THRESHOLD) {
      const ref = await uploadFileChunked(f, {
        signal,
        onProgress: (p) => {
          const overallLoaded = bytesDoneBefore + p.loaded;
          const overallPercent = Math.round((overallLoaded / totalBytes) * 100);
          onProgress && onProgress({
            fileIndex: fi,
            fileName: f.name,
            loaded: p.loaded,
            total: p.total,
            percent: p.percent,
            overallPercent,
          });
        },
      });
      uploadedRefs.push(ref);
      bytesDoneBefore += f.size;
    } else {
      const b64 = await fileToBase64(f);
      base64Files.push(b64);
      bytesDoneBefore += f.size;
      const overallPercent = Math.round((bytesDoneBefore / totalBytes) * 100);
      onProgress && onProgress({
        fileIndex: fi,
        fileName: f.name,
        loaded: f.size,
        total: f.size,
        percent: 100,
        overallPercent,
      });
    }
  }
  return { files: base64Files, uploaded_refs: uploadedRefs };
}
